import { VariantType } from '@/components/sections/builder/elements/body/product-detail/variant-picker';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useCartProduct } from '@/stores/cart';
import { IProduct, ProductItem } from '@/types/product';
import { shallow } from 'zustand/shallow';

type PillsPickerProps = {
  product: IProduct;
  variants: VariantType[];
  items: ProductItem[];
  selectedOptions: Record<string, string>;
  handleSelect: (variantId: string, optionId: string) => void;
};

export const PillsPicker = ({
  product,
  variants,
  items,
  selectedOptions,
  handleSelect,
}: PillsPickerProps) => {
  const cartStore = useCartProduct(
    (state) => ({
      setQuantity: state.setQuantity,
    }),
    shallow
  );
  const availableQty = product.available_qty as number;

  return (
    <div>
      {variants.map((variant) => (
        <div key={variant.variant_id}>
          <span className='text-sm'>{variant.label}</span>
          <ToggleGroup
            type='single'
            className='theme_variant_pill_group mb-4 mt-3 w-fit flex-wrap items-start justify-start'
            defaultValue={selectedOptions[variant.variant_id]}
            value={selectedOptions[variant.variant_id]}
            disabled={availableQty === 0}
          >
            {variant.options.map((option) => {
              const isSelected = selectedOptions[variant.variant_id] === option.option_id;

              const tempOptions = { ...selectedOptions };
              if (isSelected) {
                delete tempOptions[variant.variant_id];
              } else {
                tempOptions[variant.variant_id] = option.option_id;
              }

              const tempSelectedOption = Object.fromEntries(
                Object.entries(tempOptions).filter(([_, v]) => v)
              );

              const tempAllSelected = Object.keys(tempSelectedOption).length === variants.length;

              let isOutOfStock = false;
              if (tempAllSelected) {
                const selectedKey = Object.values(tempSelectedOption).join(':');
                const stock = items.find((item) => item.item_option_id === selectedKey)?.stock;
                isOutOfStock = stock === undefined || stock === 0;
              }

              const disabled = tempAllSelected ? isOutOfStock && !isSelected : false;

              const handleChange = () => {
                if (disabled) return;
                handleSelect(variant.variant_id, isSelected ? '' : option.option_id);
                cartStore.setQuantity(1);
              };

              return (
                <ToggleGroupItem
                  key={option.option_id}
                  className={cn(
                    'theme_variant_pill min-w-[4.8em] px-2 text-sm disabled:cursor-not-allowed',
                    disabled && 'relative overflow-hidden bg-gray-200 text-gray-400'
                  )}
                  onClick={handleChange}
                  value={option.option_id}
                  disabled={disabled}
                >
                  {disabled && !isSelected && (
                    <div
                      className='absolute inset-0 bg-gray-300 bg-[repeating-linear-gradient(135deg,transparent,transparent_10px,rgba(0,0,0,0.1)_10px,rgba(0,0,0,0.1)_20px)] opacity-30'
                      aria-hidden='true'
                    ></div>
                  )}
                  <div
                    className={cn(
                      'break-all text-sm',
                      disabled && !isSelected && 'text-muted-foreground',
                      isSelected && 'theme_variant_pill_selected'
                    )}
                  >
                    {option.value}
                  </div>
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>
      ))}
    </div>
  );
};
